import React, { useState, useCallback } from 'react';
import {
    Box,
    Switch,
    Button,
    Typography,
    Paper,
    Collapse,
    Divider,
    IconButton,
    TextField,
    FormControlLabel,
    Tooltip,
    Stack,
    FormControl,
    MenuItem,
    InputLabel,
    Select,
} from '@mui/material';
import {
    DragIndicator as DragIndicatorIcon,
    ExpandLess,
    Settings,
    Delete,
    PlayArrow,
    Stop,
    VolumeUp as VolumeUpIcon,
    Vibration as VibrationIcon
} from '@mui/icons-material';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { debounce } from 'lodash';
import { autoJsStoreUtils } from '@droid/android';

const PRE_ALARM_CONFIG_KEY = 'PRE_ALARM_ACTIONS_CONFIG';

interface PreAlarmAction {
    id: string;
    type: 'sound' | 'vibration';
    duration: number;
    resourceId?: string;
}

interface PreAlarmConfig {
    enabled: boolean;
    actions: PreAlarmAction[];
    maxWaitDuration: number;
}

const defaultSoundResources = [
    { id: 'siren_gentle', name: '缓和警报' },
    { id: 'beep_short', name: '短提示音' },
    { id: 'alarm_clock', name: '闹钟' },
];

const SortableActionItem: React.FC<{
    action: PreAlarmAction;
    isPlaying: boolean;
    onUpdate: (id: string, updates: Partial<Omit<PreAlarmAction, 'id'>>) => void;
    onRemove: (id: string) => void;
    onPreview: (resourceId?: string) => void;
    onStopPreview: () => void;
}> = ({ action, isPlaying, onUpdate, onRemove, onPreview, onStopPreview }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: action.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 1 : 'auto',
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            elevation={isDragging ? 3 : 1}
            sx={{
                p: 2,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                userSelect: 'none',
            }}
        >
            <Box {...attributes} {...listeners} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', touchAction: 'none' }}>
                <DragIndicatorIcon />
            </Box>
            
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
                    <Typography variant="subtitle2">
                        {action.type === 'sound' ? '声音' : '震动'}
                    </Typography>
                    <TextField
                        size="small"
                        type="number"
                        label="持续时间 (ms)"
                        value={action.duration}
                        onChange={(e) => onUpdate(action.id, { duration: parseInt(e.target.value, 10) || 0 })}
                        sx={{ width: '100%' }}
                    />
                    
                    {action.type === 'sound' && (
                        <FormControl size="small" sx={{ width: '100%' }}>
                            <InputLabel>声音类型</InputLabel>
                            <Select
                                value={action.resourceId || ''}
                                label="声音类型"
                                onChange={(e) => onUpdate(action.id, { resourceId: e.target.value })}
                                startAdornment={
                                    <Tooltip title={isPlaying ? "停止" : "试听"}>
                                        <IconButton
                                            size="small"
                                            onClick={() => isPlaying ? onStopPreview() : onPreview(action.resourceId)}
                                            sx={{ mr: 1 }}
                                        >
                                            {isPlaying ? <Stop fontSize="small" /> : <PlayArrow fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                }
                            >
                                {defaultSoundResources.map((sound) => (
                                    <MenuItem key={sound.id} value={sound.id}>
                                        {sound.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </Box>
            
            <IconButton onClick={() => onRemove(action.id)} size="small" color="error">
                <Delete />
            </IconButton>
        </Paper>
    );
};

const PreAlarmConfig2: React.FC = () => {
    const [config, setConfig] = useState<PreAlarmConfig | null>(null);
    const [expanded, setExpanded] = useState(false);
    const [playingSoundId, setPlayingSoundId] = useState<string | null>(null);

    const debouncedWrite = useCallback(
        debounce((newConfig: PreAlarmConfig) => {
            autoJsStoreUtils.write(PRE_ALARM_CONFIG_KEY, newConfig);
        }, 500),
        []
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    React.useEffect(() => {
        const loadConfig = async () => {
            const storedConfig = await autoJsStoreUtils.read<PreAlarmConfig>(PRE_ALARM_CONFIG_KEY);
            if (storedConfig) {
                const actionsWithIds = storedConfig.actions.map((action) => ({
                    ...action,
                    id: action.id || `action-${Math.random().toString(36).substr(2, 9)}`,
                }));
                setConfig({
                    ...storedConfig,
                    actions: actionsWithIds,
                });
            } else {
                setConfig({
                    enabled: true,
                    actions: [],
                    maxWaitDuration: 30000,
                });
            }
        };
        loadConfig();
    }, []);

    const updateConfig = useCallback(
        (updates: Partial<PreAlarmConfig>) => {
            setConfig((prev) => {
                if (!prev) return null;
                const newConfig = { ...prev, ...updates };
                debouncedWrite(newConfig);
                return newConfig;
            });
        },
        [debouncedWrite]
    );

    const handleToggleEnabled = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateConfig({ enabled: event.target.checked });
    };

    const handleAddAction = (type: 'sound' | 'vibration') => {
        const newAction: PreAlarmAction = {
            id: `action-${Date.now()}`,
            type,
            duration: 8000,
            ...(type === 'sound' && { resourceId: defaultSoundResources[0].id }),
        };
        updateConfig({
            actions: [...(config?.actions || []), newAction],
        });
    };

    const handleUpdateAction = (id: string, updates: Partial<Omit<PreAlarmAction, 'id'>>) => {
        if (!config) return;
        updateConfig({
            actions: config.actions.map((action) =>
                action.id === id ? { ...action, ...updates } : action
            ),
        });
    };

    const handleRemoveAction = (id: string) => {
        if (!config) return;
        updateConfig({
            actions: config.actions.filter((action) => action.id !== id),
        });
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!config || !over || active.id === over.id) return;

        const oldIndex = config.actions.findIndex((a) => a.id === active.id);
        const newIndex = config.actions.findIndex((a) => a.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            updateConfig({
                actions: arrayMove(config.actions, oldIndex, newIndex),
            });
        }
    };

    const handlePreviewSound = (resourceId?: string) => {
        if (!resourceId) return;
        handleStopSound();
        
        console.log(`[WebView] Attempting to preview sound. resourceId: ${resourceId}`);

        if ((window as any).SoundManager) {
            (window as any).SoundManager.previewSound(resourceId);
            setPlayingSoundId(resourceId);
        } else {
            console.warn('SoundManager JavascriptInterface is not available.');
        }
    };

    const handleStopSound = () => {
        if ((window as any).SoundManager) {
            (window as any).SoundManager.stopPreview();
        }
        setPlayingSoundId(null);
    };

    if (!config) {
        return <Box sx={{ p: 2, textAlign: 'center' }}>加载中...</Box>;
    }

    return (
        <Box sx={{ mb: 3 }}>
            <Paper 
                elevation={1} 
                sx={{ 
                    p: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1">防误触报警设置</Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.enabled}
                                onChange={handleToggleEnabled}
                                color="primary"
                            />
                        }
                        label={config.enabled ? '已启用' : '已禁用'}
                    />
                </Box>
                
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={expanded ? <ExpandLess /> : <Settings />}
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? '收起设置' : '设置'}
                </Button>
            </Paper>

            <Collapse in={expanded}>
                <Paper 
                    elevation={0} 
                    sx={{ 
                        mt: 1, 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            最大等待时间 (毫秒)
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            value={config.maxWaitDuration}
                            onChange={(e) =>
                                updateConfig({ maxWaitDuration: parseInt(e.target.value, 10) || 0 })
                            }
                            size="small"
                            InputProps={{
                                endAdornment: (
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                        毫秒
                                    </Typography>
                                ),
                            }}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2">
                                预警动作 ({config.actions.length})
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VolumeUpIcon />}
                                    onClick={() => handleAddAction('sound')}
                                >
                                    添加声音
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VibrationIcon />}
                                    onClick={() => handleAddAction('vibration')}
                                >
                                    添加震动
                                </Button>
                            </Stack>
                        </Box>

                        {config.actions.length === 0 ? (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                }}
                            >
                                <Typography>暂无报警动作，请点击上方按钮添加</Typography>
                            </Paper>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={config.actions.map((a) => a.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {config.actions.map((action) => (
                                        <SortableActionItem
                                            key={action.id}
                                            action={action}
                                            isPlaying={playingSoundId === action.resourceId}
                                            onUpdate={handleUpdateAction}
                                            onRemove={handleRemoveAction}
                                            onPreview={handlePreviewSound}
                                            onStopPreview={handleStopSound}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        )}
                    </Box>
                </Paper>
            </Collapse>
        </Box>
    );
};

export default PreAlarmConfig2;
